import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getDate, setValue} from "../../redux/slice/dateSlice";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";

const MAINTENANCE_DATE_KEY = "유지관리 업무현황";
const TASK_CATEGORY_ORDER = ["SR관리", "장애관리", "작업관리", "점검관리"];

const formatCategoryLabel = (name) => (name === "전체현황" ? "전체 현황" : name);

const calcPercent = (cnt, denominator) => {
    const total = Number(denominator) || 0;
    const done = Number(cnt) || 0;
    if (total === 0) return 0;
    return Math.round((done / total) * 100);
};

const formatCountLabel = (cnt, denominator) =>
    `${Number(cnt) || 0} / ${Number(denominator) || 0}건`;

function TaskProgressBar({percent}) {
    return (
        <div className="h-[8px] min-w-0 flex-1 overflow-hidden rounded-full bg-[#e8e8e8]">
            <div
                className="h-full rounded-full bg-[#3b82f6] transition-[width] duration-300"
                style={{width: `${Math.min(100, Math.max(0, percent))}%`}}
            />
        </div>
    );
}

function TaskCategoryCard({item, active, onSelect, formatLabel = formatCategoryLabel}) {
    const cnt = Number(item.cnt) || 0;
    const denominator = Number(item.denominator) || 0;

    return (
        <button
            type="button"
            onClick={onSelect}
            className={[
                "flex w-full items-center justify-between rounded-[8px] border px-[10px] py-[7px] text-left text-[14px] transition-colors",
                active
                    ? "border-[#3b82f6] bg-[#3b82f6] text-white"
                    : "border-[#e5e5e5] bg-white text-[#333] hover:border-[#bbb]",
            ].join(" ")}
        >
            <span className="font-medium">{formatLabel(item.name)}</span>
            <span className={active ? "text-white/95" : "text-[#666]"}>
                {formatCountLabel(cnt, denominator)}
            </span>
        </button>
    );
}

const aggregateTotals = (items) =>
    (items ?? []).reduce(
        (acc, row) => ({
            cnt: acc.cnt + (Number(row.cnt) || 0),
            denominator: acc.denominator + (Number(row.denominator) || 0),
        }),
        {cnt: 0, denominator: 0}
    );

/**
 * 유지관리 업무현황 / 서비스 업무·진행단계 현황 공통 레이아웃
 */
export function WorkloadStatusStatic({
    data,
    loading = false,
    dateKey,
    defaultTitle = "업무현황",
    periodOptions = [
        {value: 1, label: "월별"},
        {value: 0, label: "일별"},
    ],
    overallNames = ["전체현황", "전체"],
    categoryOrder = null,
    monthOverMonthErrorDelta = null,
    getSummaryLines = null,
    formatLabel = formatCategoryLabel,
}) {
    const dispatch = useDispatch();
    const period = useSelector((state) => getDate(state, dateKey));
    const items = data?.data ?? [];

    const overallItem = useMemo(() => {
        const found = items.find((row) => overallNames.includes(row.name));
        if (found) return found;
        const totals = aggregateTotals(items);
        return {name: "전체", ...totals};
    }, [items, overallNames]);

    const categoryItems = useMemo(() => {
        if (categoryOrder?.length) {
            return categoryOrder
                .map((name) => items.find((row) => row.name === name))
                .filter(Boolean);
        }
        return items.filter((row) => !overallNames.includes(row.name));
    }, [items, categoryOrder, overallNames]);

    const [selectedName, setSelectedName] = useState(
        () => categoryItems[0]?.name ?? ""
    );

    useEffect(() => {
        if (categoryItems.some((row) => row.name === selectedName)) return;
        if (categoryItems[0]?.name) setSelectedName(categoryItems[0].name);
    }, [categoryItems, selectedName]);

    const overallCnt = Number(overallItem?.cnt) || 0;
    const overallDenominator = Number(overallItem?.denominator) || 0;
    const overallPercent = calcPercent(overallCnt, overallDenominator);

    const selectedItem = useMemo(
        () => categoryItems.find((row) => row.name === selectedName) ?? categoryItems[0],
        [categoryItems, selectedName]
    );

    const defaultSummaryLines = useMemo(() => {
        if (!selectedItem) return [];
        const done = Number(selectedItem.cnt) || 0;
        const total = Number(selectedItem.denominator) || 0;
        return [
            <>
                총 {total.toLocaleString()}건 중{" "}
                {done.toLocaleString()}건을 처리했습니다.
            </>,
        ];
    }, [selectedItem]);

    const summaryLines = getSummaryLines
        ? getSummaryLines({overallItem, selectedItem, items})
        : defaultSummaryLines;

    const handlePeriodChange = (event) => {
        dispatch(setValue({keys: dateKey, value: Number(event.target.value)}));
    };

    if (!data && loading) {
        return (
            <div className="managed-task-static relative flex h-full min-h-0 flex-1 w-full flex-col bg-white">
                <ChartLoadingOverlay />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="managed-task-static flex h-full min-h-0 flex-1 w-full flex-col bg-white">
                <div className="flex shrink-0 items-center justify-between gap-3">
                    <h3 className="text-[21px] font-bold leading-none text-[#333]">
                        {defaultTitle}
                    </h3>
                </div>
                <div className="flex flex-1 items-center justify-center text-[13px] text-[#9ca3af]">
                    데이터가 없습니다.
                </div>
            </div>
        );
    }

    return (
        <div className="managed-task-static relative flex h-full min-h-0 flex-1 w-full flex-col bg-white">
            {loading && <ChartLoadingOverlay />}

            <div className="flex shrink-0 items-center justify-between gap-3">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">
                    {data?.title ?? defaultTitle}
                </h3>
                <select
                    value={String(period ?? periodOptions[0]?.value ?? 1)}
                    onChange={handlePeriodChange}
                    className="rounded-[6px] border border-[#d9d9d9] bg-white px-[12px] py-[6px] text-[12px] text-[#333] outline-none focus:border-[#3b82f6]"
                >
                    {periodOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex min-h-0 flex-1 pt-[10px]">
                <div className="flex min-h-0 min-w-0 flex-1 flex-col pr-[20px]">
                    <p className="text-[13px] text-[#888]">전체 현황</p>
                    <p className="mt-[8px] text-[48px] font-bold leading-none text-[#3b82f6]">
                        {overallPercent}%
                    </p>

                    <div className="mt-[8px] flex items-center gap-[12px]">
                        <TaskProgressBar percent={overallPercent} />
                        <span className="shrink-0 text-[13px] font-medium text-[#333]">
                            {formatCountLabel(overallCnt, overallDenominator)}
                        </span>
                    </div>

                    <ul className="mt-auto space-y-[10px] pt-[12px] text-[12px] leading-[1.5] text-[#666]">
                        {summaryLines.map((line, index) => (
                            <li key={index} className="flex gap-[8px]">
                                <span className="mt-[6px] h-[4px] w-[4px] shrink-0 rounded-full bg-[#3b82f6]" />
                                <span>{line}</span>
                            </li>
                        ))}
                        {monthOverMonthErrorDelta != null && (
                            <li className="flex gap-[8px]">
                                <span className="mt-[6px] h-[4px] w-[4px] shrink-0 rounded-full bg-[#3b82f6]" />
                                <span>
                                    전월대비 장애비율이{" "}
                                    <span className="font-semibold text-[#3b82f6]">
                                        {monthOverMonthErrorDelta > 0 ? "+" : ""}
                                        {monthOverMonthErrorDelta}%
                                    </span>{" "}
                                    {monthOverMonthErrorDelta <= 0 ? "줄었어요." : "늘었어요."}
                                </span>
                            </li>
                        )}
                    </ul>
                </div>

                <div className="w-px shrink-0 bg-[#f0f0f0]" />

                <div className="flex max-h-[280px] min-h-0 min-w-0 flex-1 flex-col gap-[4px] overflow-y-auto overscroll-contain pl-[20px] md:max-h-none">
                    {categoryItems.length > 0 ? (
                        categoryItems.map((item) => (
                            <TaskCategoryCard
                                key={item.name}
                                item={item}
                                active={selectedName === item.name}
                                onSelect={() => setSelectedName(item.name)}
                                formatLabel={formatLabel}
                            />
                        ))
                    ) : (
                        <div className="flex flex-1 items-center justify-center text-[13px] text-[#9ca3af]">
                            항목이 없습니다.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** 통합대시보드 — 유지관리 업무현황 */
export function ManagedTaskStatic({data, loading = false, monthOverMonthErrorDelta = null}) {
    return (
        <WorkloadStatusStatic
            data={data}
            loading={loading}
            dateKey={MAINTENANCE_DATE_KEY}
            defaultTitle="유지관리 업무현황"
            categoryOrder={TASK_CATEGORY_ORDER}
            monthOverMonthErrorDelta={monthOverMonthErrorDelta}
            getSummaryLines={({items}) => {
                const sr = items.find((row) => row.name === "SR관리");
                if (!sr) return [];
                const srCnt = Number(sr.cnt) || 0;
                const srTotal = Number(sr.denominator) || 0;
                return [
                    <>
                        총 {srTotal.toLocaleString()}개의 서비스 요청이 있으며,{" "}
                        {srCnt.toLocaleString()}개의 요청을 처리했습니다.
                    </>,
                ];
            }}
        />
    );
}

const SERVICE_PERIOD_OPTIONS = [
    {value: 1, label: "금월"},
    {value: 0, label: "금일"},
];

const STATUS_CATEGORY_ORDER = [
    "신청결재",
    "접수전",
    "결재",
    "검토",
    "진행",
    "검수",
    "만족도조사",
    "완료",
];

/** 서비스대시보드 — 업무별 처리현황 */
export function ServiceTaskProcessStatic({data, loading = false}) {
    return (
        <WorkloadStatusStatic
            data={data}
            loading={loading}
            dateKey="업무별 처리현황"
            defaultTitle="업무별 처리현황"
            periodOptions={SERVICE_PERIOD_OPTIONS}
            getSummaryLines={({selectedItem}) => {
                if (!selectedItem) return [];
                const done = Number(selectedItem.cnt) || 0;
                const total = Number(selectedItem.denominator) || 0;
                const pct = calcPercent(done, total);
                return [
                    <>
                        <span className="font-medium text-[#333]">{selectedItem.name}</span>{" "}
                        기준 {total.toLocaleString()}건 중 {done.toLocaleString()}건 처리
                        (처리율 {pct}%).
                    </>,
                ];
            }}
        />
    );
}

/** 서비스대시보드 — 진행단계별 현황 */
export function ServiceStatusByStageStatic({data, loading = false}) {
    return (
        <WorkloadStatusStatic
            data={data}
            loading={loading}
            dateKey="진행단계별 현황"
            defaultTitle="진행단계별 현황"
            periodOptions={SERVICE_PERIOD_OPTIONS}
            categoryOrder={STATUS_CATEGORY_ORDER}
            getSummaryLines={({selectedItem}) => {
                if (!selectedItem) return [];
                const done = Number(selectedItem.cnt) || 0;
                const total = Number(selectedItem.denominator) || 0;
                return [
                    <>
                        진행단계 「{selectedItem.name}」 {total.toLocaleString()}건 중{" "}
                        {done.toLocaleString()}건 (
                        {calcPercent(done, total)}%).
                    </>,
                ];
            }}
        />
    );
}

export default ManagedTaskStatic;
